import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, StickyNote } from "lucide-react";
import { formatRelativeTime } from "@/lib/format";
import type { InternalNote } from "@db/schema";

interface InternalNoteSectionProps {
  ticketId: string;
  notes: InternalNote[];
  onNoteAdded: () => void;
}

export function InternalNoteSection({
  ticketId,
  notes,
  onNoteAdded,
}: InternalNoteSectionProps) {
  const { toast } = useToast();
  const [content, setContent] = useState("");

  const addNoteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest(
        "POST",
        `/api/support/tickets/${ticketId}/internal-notes`,
        { content },
      );
    },
    onSuccess: () => {
      setContent("");
      toast({ title: "Note added" });
      onNoteAdded();
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to add note",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold flex items-center gap-2">
        <StickyNote className="h-4 w-4" />
        Internal Notes
      </h4>

      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes yet.</p>
      ) : (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {notes.map((note) => (
            <div
              key={note.id}
              className="rounded-md border bg-muted/50 p-3 text-sm"
            >
              <p className="whitespace-pre-wrap">{note.content}</p>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {note.authorUserId} &middot; {formatRelativeTime(note.createdAt)}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <Textarea
          placeholder="Add an internal note..."
          className="min-h-[60px]"
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => addNoteMutation.mutate()}
            disabled={addNoteMutation.isPending || !content.trim()}
          >
            {addNoteMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Add Note
          </Button>
        </div>
      </div>
    </div>
  );
}
